import { useSyncExternalStore } from 'react';

/**
 * The setup sandbox, as this page knows it (APP-096). The server names every sandbox page in a
 * meta tag with the generation it was served in. It's read once, the first time it's asked for,
 * and never again: a page opened before a reset keeps its old generation even after another tab
 * signs in to the new one, so every change it sends is refused instead of landing in the new
 * sandbox. Null on every other environment.
 */
export interface SetupPage {
  /** The generation this page was served in. */
  generation: string;
  /** Where the working environment is, for the banner's link. */
  workingOrigin: string;
}

let page: SetupPage | null | undefined;

export function setupPage(): SetupPage | null {
  if (page !== undefined) return page;
  const meta =
    typeof document === 'undefined'
      ? null
      : document.querySelector<HTMLMetaElement>('meta[name="myrewards-setup"]');
  const generation = meta?.dataset['generation'] ?? '';
  const workingOrigin = meta?.dataset['workingOrigin'] ?? '';
  page =
    /^\d{1,9}$/.test(generation) && /^https?:\/\/[^/]+$/.test(workingOrigin)
      ? { generation, workingOrigin }
      : null;
  if (page !== null) forgetEarlierGenerations(page.generation);
  return page;
}

/** Where this browser remembers which sandbox generation its saved state belongs to. */
const GENERATION_KEY = 'mrv2.setupGeneration';

/**
 * A reset discards the sandbox's people and awards, so what this browser kept for the sandbox
 * (Search's recent pages, header choices by person) points at nothing. On the first page of a new
 * generation it's cleared: only the app's own `mrv2.` keys, only on the sandbox's origin (no other
 * page has the meta), and only once per generation.
 */
function forgetEarlierGenerations(generation: string): void {
  try {
    const storage = window.localStorage;
    if (storage.getItem(GENERATION_KEY) === generation) return;
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key?.startsWith('mrv2.') === true) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
    storage.setItem(GENERATION_KEY, generation);
  } catch {
    // Storage blocked or full: nothing was kept to clear.
  }
}

/** The header every change carries in the sandbox; the server refuses one without it. */
export const GENERATION_HEADER = 'x-myrewards-generation';

let stale = false;
const listeners = new Set<() => void>();

/** The server said this page is from before a reset: the banner offers a reload. */
export function markSetupPageStale(): void {
  if (stale) return;
  stale = true;
  for (const l of listeners) l();
}

export function useSetupPageStale(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => stale,
    () => false,
  );
}

/** Tests only: forget what was read, as a fresh page load would. */
export function resetSetupPageForTests(): void {
  page = undefined;
  stale = false;
}
